/* CI026 - Charles & Ivy - Register a product configurator tool
 * ---------------------------------------------------------------------------
 * Exposes a read-only `product_builder` tool to an in-browser AI agent.
 *
 * WHY THIS EXISTS
 * Product pages build a complete product across 8-12 steps. The standard
 * product tool returns only the base product's own variants, so an agent told
 * to add variants[0].id orders a bare panel and omits everything configured.
 *
 * WHAT IT DOES AND DOES NOT CLAIM
 * The theme assembles the real cart payload inside ProductForm.handleFormSubmit
 * (assets/theme.js), which is ~1150 lines of per-range logic and cannot be
 * called read-only. This tool therefore reports the CONFIGURATION, not the
 * basket payload, and tells the agent to submit the page's own add-to-cart
 * rather than constructing cart lines itself. Three things it deliberately
 * does not try to reproduce:
 *   - fixing kits, whose quantity is derived (mainQty + 1 + additional posts)
 *   - the angle fixing kit, keyed off installation method and post colour
 *   - additional posts, which are NOT a line item: they increment the
 *     quantity of the post line already in the build
 *
 * SAFETY: read-only. No DOM writes, no globals, no network, no basket changes.
 * Prices are in PENCE (Liquid {{ variant.price }}), unlike the Boost API.
 */

(function () {
    'use strict';
  
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
  
      function pence(v) {
        if (v === null || v === undefined || v === '') return null;
        var n = parseInt(v, 10);
        return isNaN(n) ? null : n;
      }
  
      function tidy(s) { return (s || '').replace(/\s+/g, ' ').trim(); }
  
      // Steps are hidden by hide-step, hidden, !hidden, hide, and by a
      // third-party experimentation app (c3x_*_hidden_step). Test what is
      // actually rendered rather than matching class names we do not control.
      function isVisible(el) {
        try { return !!(el.getClientRects && el.getClientRects().length); }
        catch (e) { return true; }
      }
  
      function handleFrom(path) {
        var m = (path || '').match(/\/products\/([^/?#]+)/);
        return m ? m[1] : null;
      }
  
      // Half the .product-step divs carry data-step; the rest need the shared
      // step-header. Its h3.title also holds a required asterisk and a
      // span.result containing the CURRENT SELECTION, both of which must go.
      function stepLabel(el) {
        var attr = tidy(el.getAttribute('data-step')).replace(/:$/, '');
        if (attr) return attr;
        var h = el.querySelector('.step-header h3.title, .step-header .title, .step-title, h2, h3');
        if (!h) return '';
        var clone = h.cloneNode(true); // detached copy - the page is untouched
        try {
          var junk = clone.querySelectorAll('.result, .count-wrapper, .step-tooltip');
          for (var i = 0; i < junk.length; i++) junk[i].remove();
        } catch (e) { /* ignore */ }
        return tidy(clone.textContent).replace(/\*/g, '')
          .replace(/^\d+\s*\.\s*/, '').split(':')[0].trim();
      }
  
      function readStep(el, index) {
        var links = [].slice.call(el.querySelectorAll('a.step-item')).map(function (a) {
          return {
            label: tidy(a.textContent),
            handle: handleFrom(a.getAttribute('href') || ''),
            current: /(^|\s)active(\s|$)/.test(a.className || '')
          };
        });
  
        var radios = [].slice.call(el.querySelectorAll('input[type=radio]'))
          .filter(function (r) { return r.name && !/tooltip-tab/.test(r.name); });
  
        var names = radios.map(function (r) { return r.name; })
          .filter(function (n, k, a) { return a.indexOf(n) === k; });
  
        var groups = names.map(function (name) {
          var inGroup = radios.filter(function (r) { return r.name === name; });
          var on = inGroup.filter(function (r) { return r.checked; })[0];
          return {
            name: name,
            selected: on ? on.value : null,
            choices: inGroup.map(function (r) {
              var lab = r.id ? el.querySelector('label[for="' + r.id + '"]') : null;
              return {
                value: r.value,
                label: lab ? tidy(lab.textContent) : null,
                // Two conventions coexist: data-variant-id on bespoke step
                // blocks, data-product-variant on generic .extra-product-option
                // items (letterboxes, handles, spiked toppings).
                variant_id: r.getAttribute('data-variant-id') ||
                            r.getAttribute('data-product-variant') || null,
                extra_cost: pence(r.getAttribute('data-price') ||
                                  r.getAttribute('data-quantity-price') ||
                                  r.getAttribute('data-product-cost'))
              };
            })
          };
        });
  
        // Some steps render both link items and radios; report both rather than
        // letting links win and silently dropping the options.
        var kind = 'info';
        if (links.length && groups.length) kind = 'mixed';
        else if (links.length) kind = 'product';
        else if (groups.length) kind = 'choice';
  
        var q = el.querySelector('input[name="quantity"], input[id^="Quantity-"]');
  
        return {
          step: index + 1,
          label: stepLabel(el),
          kind: kind,
          links: links,
          groups: groups,
          quantity: q ? (parseInt(q.value, 10) || null) : null
        };
      }
  
      function readBuilder() {
        var steps = [].slice.call(document.querySelectorAll('.product-step'))
          .filter(isVisible);
        var read = steps.map(readStep);
  
        var form = document.querySelector('form.main-product-form') ||
                   document.querySelector('form[action^="/cart/add"]');
        var idEl = form && form.querySelector('input[name="id"], select[name="id"]');
        var qtyEl = form && form.querySelector('input[name="quantity"]');
        var baseQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
  
        var priceBlock = document.querySelector('.product__block--price');
        var priceEl = priceBlock && (
          priceBlock.querySelector('.f-price__sale .f-price-item--sale[data-price]') ||
          priceBlock.querySelector('.f-price-item[data-price]')
        );
        var basePrice = priceEl ? pence(priceEl.getAttribute('data-price')) : null;
  
        // Extras that are genuinely their own basket line, i.e. a selected
        // choice that names a variant.
        var extras = [];
        read.forEach(function (s) {
          s.groups.forEach(function (g) {
            var c = (g.choices || []).filter(function (x) { return x.value === g.selected; })[0];
            if (c && c.variant_id) {
              extras.push({
                step: s.label,
                choice: c.label || c.value,
                variant_id: c.variant_id,
                extra_cost: c.extra_cost,
                quantity: s.quantity || 1
              });
            }
          });
        });
  
        // Selected extras that carry a price but no variant: these adjust an
        // existing line's quantity rather than adding one (additional posts).
        var modifiers = [];
        read.forEach(function (s) {
          s.groups.forEach(function (g) {
            var c = (g.choices || []).filter(function (x) { return x.value === g.selected; })[0];
            if (c && !c.variant_id && c.extra_cost) {
              modifiers.push({
                step: s.label,
                choice: c.label || c.value,
                extra_cost: c.extra_cost,
                quantity: s.quantity || 1,
                note: 'Adjusts the quantity of a line already in the build ' +
                  'rather than adding a separate product.'
              });
            }
          });
        });
  
        var extrasTotal = extras.concat(modifiers).reduce(function (a, x) {
          return a + ((x.extra_cost || 0) * (x.quantity || 1));
        }, 0);
  
        return {
          handle: handleFrom(location.pathname),
          variant_id: idEl ? idEl.value : null,
          quantity: baseQty,
          base_price: basePrice,
          base_price_display: priceEl ? tidy(priceEl.textContent) : null,
          configured_extras: extras,
          quantity_modifiers: modifiers,
          estimated_total: basePrice === null ? null : (basePrice * baseQty) + extrasTotal,
          currency: 'GBP',
          price_units: 'pence',
          step_count: read.length,
          steps: read,
          how_to_order: 'Submit the page\'s own add to cart. The theme builds ' +
            'the multi-line basket itself, including fixing kits whose ' +
            'quantities are derived and are not listed here. Do not construct ' +
            'cart lines from this response.',
          totals_are_estimates: true
        };
      }
  
      function definition() {
        return {
          name: 'product_builder',
          description: 'Read the step-by-step configurator on the current ' +
            'product page. Returns every visible step with its label, choices ' +
            'and current selection, the base variant and quantity, the extras ' +
            'the configuration adds as separate basket lines, and an estimated ' +
            'total in pence. Step names and counts vary by product range and ' +
            'are returned rather than assumed. Read-only. IMPORTANT: the base ' +
            'variant alone is not the product - the configuration adds further ' +
            'lines. Order by submitting the page add-to-cart, not by building ' +
            'cart lines from this response, because some lines (fixing kits) ' +
            'have derived quantities this tool does not compute. Steps that ' +
            'link to another product return a handle; navigate there and call ' +
            'this tool again, as the earlier snapshot no longer applies.',
          readOnlyHint: true,
          inputSchema: {
            type: 'object',
            properties: {
              include_steps: {
                type: 'boolean',
                description: 'Return the full step list as well as the summary.',
                default: true
              }
            }
          },
          execute: function (input) {
            try {
              if (!document.querySelector('.product-step')) {
                return Promise.resolve({
                  builder: false,
                  note: 'This product page has no step configurator. Use the ' +
                    'standard product tool for its options.'
                });
              }
              var r = readBuilder();
              if (input && input.include_steps === false) delete r.steps;
              r.builder = true;
              return Promise.resolve(r);
            } catch (err) {
              return Promise.resolve({
                error: 'builder_unreadable',
                note: 'The configurator could not be read. Do not assume the ' +
                  'base variant is the whole product. (' +
                  (err && err.message ? err.message : 'unknown') + ')'
              });
            }
          }
        };
      }
  
      var attempts = 0;
      function tryRegister() {
        try {
          var mc = document.modelContext;
          if (mc && typeof mc.registerTool === 'function') {
            mc.registerTool(definition());
            return;
          }
        } catch (err) { return; }
        if (++attempts < 4) setTimeout(tryRegister, 500);
      }
      tryRegister();
    } catch (err) {
      // Enhancement only - never allow this to affect the storefront.
    }
  })();